import {css} from '@mui/material';
import {TooltipWrapperCore} from '@wallaby-ui/core';
import React from 'react';
import {useIntl} from 'react-intl';

import {formatDayMonth} from './alert_format';
import {ALERT_DISPLAYED_STATE_ENUM, AlertEpisodeDisplayedState} from './alert_types';
import {useFormatters} from '../LightFormatter';

type Props = {
  state: AlertEpisodeDisplayedState;
  /** Shown as "Snoozed · DD.MM" when the state is SNOOZED. */
  snoozeExpiresOn?: string | null;
  /** 'panel' is the panel's 11px chip; 'list' the alerts list's 12px chip, Inactive and Deleted in the darker grey text. */
  variant?: 'panel' | 'list';
  style?: React.CSSProperties;
};

/* Chip palette of the design handoff (CLAUDE.md): dark grey text on a tint, no dot, the radius of the app's chips
   (DlMuiChip, e.g. the pharmacotherapy recommendations' patient riskiness). Inactive and Deleted share the grey tint
   with a lighter text. */
const chipCss = css`
  display: inline-flex;
  align-items: center;
  font-family: var(--dlFontFamilySemiBold);
  font-size: 11px;
  line-height: 1.4;
  border-radius: 2px;
  padding: 2px 8px;
  white-space: nowrap;
  color: var(--dlThemeGray-600);
  &[data-state='ACTIVE'] {
    background: var(--dlAlertActiveBackground);
  }
  &[data-state='SNOOZED'] {
    background: var(--dlAlertSnoozedBackground);
  }
  &[data-state='SOLVED'] {
    background: var(--dlAlertSolvedBackground);
  }
  &[data-state='INACTIVE'],
  &[data-state='DELETED'] {
    background: var(--dlThemeGray-50);
    color: var(--dlThemeGray-500);
  }
  /* the Alerts Table design: 12px, a wider pad, Inactive and Deleted on var(--dlAlertNeutralBackground) in the same text grey as the rest */
  &[data-variant='list'] {
    font-size: 12px;
    padding: 2px 9px;
  }
  &[data-variant='list'][data-state='INACTIVE'],
  &[data-variant='list'][data-state='DELETED'] {
    background: var(--dlAlertNeutralBackground);
    color: var(--dlThemeGray-600);
  }
`;

/** The displayed state of an episode as a chip, shared by the Alerts panel and the Alerts list page. */
export const AlertStateChip: React.FC<Props> = (props) => {
  const {state, snoozeExpiresOn, variant = 'panel', style} = props;
  const intl = useIntl();
  const {tEnum} = useFormatters();
  let label = tEnum(ALERT_DISPLAYED_STATE_ENUM, state);
  if (state === 'SNOOZED' && snoozeExpiresOn) label = `${label} · ${formatDayMonth(snoozeExpiresOn)}`;
  const chip = (
    <span css={chipCss} style={style} data-state={state} data-variant={variant} data-name={'dl-alert-state-chip'}>
      {label}
    </span>
  );
  // the Inactive chip explains itself: the episode no longer matches its filter (PDW-182)
  if (state === 'INACTIVE') {
    return (
      <TooltipWrapperCore tooltip={intl.formatMessage({id: 'alert.state.INACTIVE.tooltip'})} noPadding>
        {chip}
      </TooltipWrapperCore>
    );
  }
  return chip;
};

AlertStateChip.displayName = 'AlertStateChip';
